import { AfterViewChecked, Component, computed, ElementRef, HostListener, inject, TemplateRef, viewChild, viewChildren } from '@angular/core';
import { Router } from '@angular/router';
import { CartComponent } from "../../menu/cart/cart.component";
import { ItemEditorComponent } from "../item-editor/item-editor.component";
import { SigningModalComponent } from "../signing-modal/signing-modal.component";
import { EditWalletModalComponent } from "../../shared/edit-wallet-modal/edit-wallet-modal.component";
import { AddWalletModalComponent } from "../../shared/add-wallet-modal/add-wallet-modal.component";
import { EditAddressModalComponent } from "../../shared/edit-address-modal/edit-address-modal.component";
import { ModalService, ModalName } from '../services/modal.service';
import { CommonModule } from '@angular/common';
import { ImagePreviewModalComponent } from "../../shared/image-preview-modal/image-preview-modal.component";
import { LayoutService } from '../services/layout.service';
import { OrderDetailsModalComponent } from "../order-details-modal/order-details-modal.component";
import { MobileNavMenuComponent } from "../mobile-nav-menu/mobile-nav-menu.component";
import { AddAddressModalComponent } from "../../shared/add-address-modal/add-address-modal.component";
import { AddCouponModalComponent } from "../../shared/add-coupon-modal/add-coupon-modal.component";
import { VerifyEmailModalComponent } from "../../account/verify-email-modal/verify-email-modal.component";
import { CouponConfirmationModalComponent } from "../../shared/coupon-conformation-modal/coupon-confirmation-modal.component";
import { OrderTreeNotificationModalComponent } from "../../shared/order-tree-notification-modal/order-tree-notification-modal.component";
import { GenericModalsComponent } from "../generic-modals/generic-modals.component";
import { ForcedChoiceModalsComponent, EnhancedGenericModalContext } from "../forced-choice-modals/forced-choice-modals.component";
import { OrderService } from '../services/order.service';
import { trigger, style, transition, animate, query, group } from '@angular/animations';
import { SpecialItemEditorComponent } from "../special-item-editor/special-item-editor.component";
import { SmsOfferModalComponent } from '../../shared/sms-offer-modal/sms-offer-modal.component';
import { CompletedSurveyModalComponent } from "../../survey/completed-survey-modal/completed-survey-modal.component";
import { StoreIdService } from '../services/store-id.service';
import { A11yModule } from '@angular/cdk/a11y';
import { FundraiserSponsorsModalComponent } from "../../shared/fundraiser-sponsors-modal/fundraiser-sponsors-modal.component";
import { CancelSubscriptionModalComponent } from "../../loyalty/premium/subscription-management/cancel-subscription-modal/cancel-subscription-modal.component";
import { ReactivateSubscriptionModalComponent } from "../../loyalty/premium/subscription-management/reactivate-subscription-modal/reactivate-subscription-modal.component";
import { ModalSurveyCheckoutComponent } from '../../checkout/checkout-enjoy/modal-survey-checkout/modal-survey-checkout.component';
import { PremiumPromoModalComponent } from "../../shared/premium-promo-modal/premium-promo-modal.component";
import { DotsLoaderComponent } from "../../shared/dots-loader/dots-loader.component";
import { ModalAutoFocusDirective } from '../../shared/directives/modal-auto-focus.directive';

@Component({
  selector: 'cwo-modals',
  standalone: true,
  imports: [
    CommonModule,
    CartComponent,
    ItemEditorComponent,
    SigningModalComponent,
    EditWalletModalComponent,
    AddWalletModalComponent,
    EditAddressModalComponent,
    ImagePreviewModalComponent,
    OrderDetailsModalComponent,
    MobileNavMenuComponent,
    AddAddressModalComponent,
    AddCouponModalComponent,
    VerifyEmailModalComponent,
    CouponConfirmationModalComponent,
    OrderTreeNotificationModalComponent,
    GenericModalsComponent,
    ForcedChoiceModalsComponent,
    SpecialItemEditorComponent,
    SmsOfferModalComponent,
    CompletedSurveyModalComponent,
    A11yModule,
    FundraiserSponsorsModalComponent,
    CancelSubscriptionModalComponent,
    ReactivateSubscriptionModalComponent,
    ModalSurveyCheckoutComponent,
    PremiumPromoModalComponent,
    DotsLoaderComponent,
    ModalAutoFocusDirective
],
  templateUrl: './modals.component.html',
  styleUrl: './modals.component.scss',
  animations: [
    trigger(
      'enterAnimation', [
      transition(':enter', group([
        query('.from-top', [
          style({ transform: 'translateY(-30%)'}),
          animate('200ms', style({ transform: 'translateY(0%)' }))
        ],{optional: true, limit: 1}),
        query('.from-bottom', [
          style({ transform: 'translateY(100%)'}),
          animate('200ms', style({ transform: 'translateY(0%)' }))
        ],{optional: true, limit: 1}),
        query('.from-right', [
          style({ transform: 'translateX(100%)'}),
          animate('200ms ease-in-out', style({ transform: 'translateX(0%)' }))
        ],{optional: true}),
        query('.from-left', [
          style({ transform: 'translateX(-100%)'}),
          animate('200ms ease-in-out', style({ transform: 'translateX(0%)' }))
        ],{optional: true}),
        style({ opacity: 0 }),
        animate('200ms', style({ opacity: 1 })),
      ])),
      transition(':leave', group([        
        query('.from-top', [
          style({ transform: 'translateY(0%)' }),
          animate('200ms', style({ transform: 'translateY(-30%)'}))
        ], {optional: true}),      
        query('.from-bottom', [
          style({ transform: 'translateY(0%)' }),
          animate('200ms', style({ transform: 'translateY(100%)'}))
        ], {optional: true}),
        query('.from-right', [
          style({ transform: 'translateX(0%)'}),
          animate('200ms ease-in-out', style({ transform: 'translateX(100%)' }))
        ],{optional: true}),
        query('.from-left', [
          style({ transform: 'translateX(0%)'}),
          animate('200ms ease-in-out', style({ transform: 'translateX(-100%)' }))
        ],{optional: true}),
        style({ opacity: 1 }),
        animate('200ms', style({ opacity: 0 })),
      ]))
    ]
    ),
  ],
})
export class ModalsComponent implements AfterViewChecked{
  modalService = inject(ModalService);
  layoutService = inject(LayoutService);
  orderService = inject(OrderService);
  router = inject(Router);
  storeId = inject(StoreIdService).storeId;

  public readonly couponConfirmationInstructionsModalTemplateRef = viewChild.required<TemplateRef<any>>('couponConfirmationInstructionsModal');

  readonly modalContainers = viewChildren<ElementRef<HTMLElement>>('modalContainer');

  modalName = ModalName;
  private previousModalCount = 0;

  private readonly timerFocusedModals = new Set<ModalName>([ModalName.Generic, ModalName.ForcedChoice]);

  ngAfterViewChecked() {
    const modals = this.modalService.modals();
    const currentCount = modals.length;
    const newestModal = modals[currentCount - 1];
    
    if (currentCount > this.previousModalCount && this.timerFocusedModals.has(newestModal?.name)) {
      setTimeout(() => {
        this.focusFirstElement();
      }, 400);// Delay to allow the animation to complete else it breaks the animation
    }
    
    this.previousModalCount = currentCount;
  }

  private focusFirstElement() {
    const modals = this.modalContainers();
    if (modals.length === 0) return;
    
    const latestModal = modals[modals.length - 1].nativeElement;
    const focusInitial = latestModal.querySelector('[cdkFocusInitial]') as HTMLElement;
    if (focusInitial) {
      focusInitial.focus();
      return;
    }

    const firstFocusable = latestModal.querySelector(
      'h1, h2, h3, p, button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"]):not(.cdk-focus-trap-anchor)'
    ) as HTMLElement;
    
    if (firstFocusable) {
      firstFocusable.focus();
    } else {
      latestModal.focus();
    }
  }

  isModalOpen(name?: ModalName): boolean {
    if (name) {
      return !!this.modalService.modals().find(modal => modal.name === name);
    }
    return this.modalService.modals().length > 0;
  }

  stateForModal = (name: ModalName): any => this.modalService.modals().find(modal => modal.name === name)?.state;

  getCurrentModalName(): ModalName | undefined {
    const modals = this.modalService.modals();
    const lastModal = modals[modals.length - 1]
    if (lastModal?.name === ModalName.Generic) {
      return lastModal.state.modalTitle;
    }
    return modals.length > 0 ? lastModal.name : undefined;
  }

  isMobile = computed(() => this.layoutService.state().viewport === 'mobile');
  isXsMobile = computed(() => this.layoutService.state().viewport === 'mobile' && this.layoutService.state().viewportSize === 'xs');

  isWithHeader = (openModalName: ModalName) => openModalName === ModalName.MobileSideNav || (openModalName === ModalName.Cart && !this.isMobile());

  getModalClassAnimation = (name: ModalName) : any => {
    const modalContentStyleClass = {'full-width': this.isFullWidthModal(name)};
    switch (name) {
      case ModalName.MobileSideNav:
        return {'from-left': true, 'absolute-left': true, ...modalContentStyleClass};
      case ModalName.Cart:
        return {'from-right': true, 'absolute-right': true, ...modalContentStyleClass};
      case ModalName.OrderTreeNotification:
      case ModalName.CouponConfirmation:
        if(this.isXsMobile()) {
          return {'from-bottom': true, ...modalContentStyleClass};
        }
        return {'from-top': true, 'absolute-top-right': true, ...modalContentStyleClass};
      case ModalName.EnjoySurvey:
        if(this.isMobile()) {
          return {'from-bottom': true, ...modalContentStyleClass};
        }
        return {'from-right': true, 'anchored-bottom-right': true, ...modalContentStyleClass};
      default:
        if(this.isMobile() && this.isWithBottomAlign(name)) {
          return {'from-bottom': true, ...modalContentStyleClass};
        }
        return {'from-top': true, ...modalContentStyleClass};
    }
  }

  handleBackdropClick(modalName: ModalName) {
    if (modalName === ModalName.ForcedChoice) {
      const modalContext = this.modalService.stateForModal(modalName) as EnhancedGenericModalContext;
      
      switch (modalContext?.backdropAction) {
        case 'prevent':
          return;
        case 'redirect-root': 
            this.modalService.closeModal(modalName); 
            this.router.navigate([this.storeId, 'menu']);
          return;
        case 'custom':
          if (modalContext.onBackdropClick) {
            modalContext.onBackdropClick();
          }
          return;
        case 'close':
        default:
          break;
      }
    }
    
    this.handleEscapeKey();
  }

  isWithBottomAlign = (openModalName: ModalName) => {
    return !![
      ModalName.OrderDetails,
      ModalName.SignIn,
      ModalName.EditWallet,
      ModalName.AddWallet,
      ModalName.AddAddress,
      ModalName.EditAddress,
      ModalName.AddCoupon,
      ModalName.OrderTreeNotification,
      ModalName.CouponConfirmation,
      ModalName.Generic,
      ModalName.ForcedChoice,
      ModalName.VerifyEmail,
      ModalName.SMSOffer,
      ModalName.SurveySubmittedLinks,
      ModalName.CancelSubscription,
      ModalName.ReactivateSubscription,
      ModalName.EnjoySurvey,
      ModalName.PremiumPromo
    ].find(name => name === openModalName);
  };

  isFullWidthModal = (openModalName: ModalName) => {
    return !![
      ModalName.SignIn,
      ModalName.AddWallet,
      ModalName.AddCoupon,
      ModalName.OrderDetails,
      ModalName.Generic,
      ModalName.ForcedChoice,
      ModalName.VerifyEmail,
      ModalName.AddAddress,
      ModalName.EditAddress,
      ModalName.OrderTreeNotification,
      ModalName.CouponConfirmation,
      ModalName.ItemEditor,
      ModalName.SpecialItemEditor,
      ModalName.SurveySubmittedLinks,
      ModalName.FundraiserSponsors,
      ModalName.CancelSubscription,
      ModalName.ReactivateSubscription,
      ModalName.EnjoySurvey
    ].find(name => name === openModalName);
  };

  @HostListener('document:keydown.escape')
  handleEscapeKey() {
    const currentModalName = this.getCurrentModalName();
    if (currentModalName === ModalName.EnjoySurvey) return;
    this.modalService.closeModal(currentModalName, { canceled: true });
  }
}
